import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, withDefaults } from 'vue'
import { convertHours24To12, isAMOrPM } from '@/utils/datetime'
import { DateTime } from 'luxon'

interface Props {
  /** Label for the time picker */
  label?: string

  /** Placeholder text for the input field */
  placeholder?: string

  /** ID for the input field */
  id?: string

  /** Validation rules for the input */
  rules?: Array<(value: any) => boolean | string>

  /** Whether to hide details below the input field */
  hideDetails?: boolean

  /** The currently selected time value, formatted as HH:mm (24-hour format) */
  value: string

  /** The display value shown in the input field */
  displayValue?: string

  /**
   * Interval in minutes for selectable times (e.g., 15 for 15-minute intervals)
   * Defaults to 15.
  */
  intervalMinutes?: number

  /**
   * Minimum allowed time, formatted as HH:mm:ss.SSS (24-hour format).
   * Defaults to "00:00:00.000".
   */
  minTime?: string

  /**
   * Maximum allowed time, formatted as HH:mm:ss.SSS (24-hour format).
   * Defaults to "23:59:59.999".
   */
  maxTime?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CUTimePickerList',
  props: {
    label: { default: '' },
    placeholder: { default: '' },
    id: { default: '' },
    rules: { default: () => [] },
    hideDetails: { type: Boolean, default: false },
    value: null,
    displayValue: null,
    intervalMinutes: { default: 15 },
    minTime: { default: '00:00:00.000' },
    maxTime: { default: '23:59:59.999' }
  },
  emits: ['input'],
  setup(__props: any, { emit }) {

const props = __props as { label: string, placeholder: string, id: string, rules: Array<(value: any) => boolean | string>, hideDetails: boolean, value: string, displayValue?: string, intervalMinutes: number, minTime: string, maxTime: string };

const HOURS_IN_DAY = 24
const MINUTES_IN_HOUR = 60





const isOpen = ref(false)

const minDateTime = computed(() => props.minTime ? DateTime.fromISO(props.minTime) : null)
const maxDateTime = computed(() => props.maxTime ? DateTime.fromISO(props.maxTime) : null)

const inputDisplayText = computed(() => props.displayValue || props.value)
const timeOptions = computed(() => {
  const options = []
  for (let hour = 0; hour < HOURS_IN_DAY; hour++) {
    for (let minute = 0; minute < 60; minute += props.intervalMinutes) {
      const formattedHour = hour.toString().padStart(2, '0')
      const formattedMinute = minute.toString().padStart(2, '0')

      // Create a DateTime object for filtering
      const currentTime = DateTime.fromObject({ hour, minute })

      // Check bounds
      if (
        (minDateTime.value && currentTime < minDateTime.value) ||
        (maxDateTime.value && currentTime > maxDateTime.value)
      ) {
        continue // Skip times outside bounds
      }

      // Use utility functions for conversion
      const hour12 = convertHours24To12(hour)
      const period = isAMOrPM(hour)

      options.push({
        text: `${hour12}:${formattedMinute} ${period}`, // Use 12-hour format with AM/PM
        value: `${formattedHour}:${formattedMinute}`, // Keep value in 24-hour format
      })
    }
  }
  return options
})

const onInput = (value: string) => {
  emit('input', value)
  isOpen.value = false
}

return { __sfc: true,HOURS_IN_DAY, MINUTES_IN_HOUR, props, emit, isOpen, minDateTime, maxDateTime, inputDisplayText, timeOptions, onInput }
}

})